import React, { useEffect, useState, useRef } from "react";
import api, { wsUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Send, Plus, Hash, MessagesSquare, Users as UsersIcon, Paperclip } from "lucide-react";

export default function ChatPage() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: "", type: "channel", is_private: false, member_ids: [] });
  const wsRef = useRef(null);
  const listRef = useRef(null);

  const loadChannels = async () => setChannels((await api.get("/chat/channels")).data);
  const loadUsers = async () => { try { setUsers((await api.get("/users")).data); } catch (_e) {} };
  useEffect(() => { loadChannels(); loadUsers(); }, []);

  useEffect(() => {
    if (!activeChannel) return;
    const load = async () => {
      const r = await api.get(`/chat/messages/${activeChannel.id}`);
      setMessages(r.data);
      setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
    };
    load();
    // WS with polling fallback
    if (wsRef.current) wsRef.current.close();
    const token = localStorage.getItem("token");
    let ws = null;
    try {
      ws = new WebSocket(wsUrl(activeChannel.id, token));
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "message") {
            setMessages((prev) => (prev.some(m => m.id === msg.data.id) ? prev : [...prev, msg.data]));
            setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
          }
        } catch (_e) {}
      };
      wsRef.current = ws;
    } catch (_err) {
      console.warn("WS connection failed, using polling fallback");
    }

    const pollInterval = setInterval(async () => {
      try {
        const r = await api.get(`/chat/messages/${activeChannel.id}`);
        setMessages((prev) => {
          const ids = new Set(prev.map(m => m.id));
          const newMsgs = r.data.filter(m => !ids.has(m.id));
          if (newMsgs.length > 0) {
            setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
            return [...prev, ...newMsgs];
          }
          return prev;
        });
      } catch (_e) {}
    }, 3000);

    return () => {
      if (ws) ws.close();
      clearInterval(pollInterval);
    };
  }, [activeChannel]);

  const send = async () => {
    if (!input.trim() || !activeChannel) return;
    try {
      await api.post("/chat/messages", { channel_id: activeChannel.id, content: input });
      setInput("");
    } catch (err) { toast.error("Fehler"); }
  };

  const createChannel = async () => {
    try { await api.post("/chat/channels", form); toast.success("Channel angelegt"); setDialog(false); setForm({ name: "", type: "channel", is_private: false, member_ids: [] }); await loadChannels(); }
    catch (err) { toast.error("Fehler"); }
  };

  const startDirect = async (uid) => {
    try {
      const r = await api.post(`/chat/direct/${uid}`);
      await loadChannels();
      setActiveChannel(r.data);
    } catch (err) { toast.error("Fehler"); }
  };

  const userName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : "?";
  };
  const channelName = (c) => {
    if (c.type === "direct") {
      const other = c.member_ids.find((x) => x !== user.id);
      return userName(other) || "Direkt";
    }
    return c.name;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 h-[calc(100vh-14rem)]" data-testid="chat-root">
      {/* Sidebar */}
      <Card className="border-border/60 lg:col-span-1 overflow-hidden flex flex-col">
        <CardContent className="p-3 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-500">Channels</h3>
            <Button data-testid="chat-new-channel" size="icon" variant="ghost" onClick={() => setDialog(true)}><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1">
            {channels.filter(c => c.type !== "direct").map((c) => (
              <button
                key={c.id}
                data-testid={`chat-ch-${c.id}`}
                onClick={() => setActiveChannel(c)}
                className={`w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-left ${activeChannel?.id === c.id ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}
              >
                <Hash className="w-4 h-4" />
                <span className="truncate">{c.name}</span>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-500 mb-2">Direkt</h3>
            <div className="space-y-1">
              {users.filter((u) => u.id !== user.id).slice(0, 30).map((u) => (
                <button
                  key={u.id}
                  data-testid={`chat-dm-${u.id}`}
                  onClick={() => startDirect(u.id)}
                  className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 text-left"
                >
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-[10px] font-bold">
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </div>
                  <span className="truncate">{u.first_name} {u.last_name}</span>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card className="border-border/60 lg:col-span-3 flex flex-col overflow-hidden">
        {activeChannel ? (
          <>
            <div className="border-b border-border/60 px-4 py-3 flex items-center gap-2">
              {activeChannel.type === "direct" ? <MessagesSquare className="w-4 h-4" /> : <Hash className="w-4 h-4" />}
              <h3 className="font-heading font-bold">{channelName(activeChannel)}</h3>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((m) => (
                <div key={m.id} className={`flex gap-2 ${m.user_id === user.id ? "flex-row-reverse" : ""}`}>
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-xs font-bold shrink-0">
                    {userName(m.user_id).split(" ").map(x => x[0]).join("")}
                  </div>
                  <div className={`max-w-md rounded-2xl px-3 py-2 text-sm ${m.user_id === user.id ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`}>
                    {m.user_id !== user.id && <div className="text-[10px] font-bold opacity-70 mb-0.5">{userName(m.user_id)}</div>}
                    <div className="whitespace-pre-wrap">{m.content}</div>
                    <div className={`text-[10px] mt-0.5 ${m.user_id === user.id ? "text-white/70" : "text-slate-500"}`}>
                      {new Date(m.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border/60 p-3 flex gap-2">
              <Input
                data-testid="chat-input"
                placeholder="Nachricht schreiben…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              />
              <Button data-testid="chat-send" onClick={send} className="rounded-full"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        ) : (
          <div className="flex-1 grid place-items-center text-slate-500 text-sm">Channel auswählen</div>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Neuer Channel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name (z.B. Team Hauptapotheke)" data-testid="chat-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="flex items-center gap-2">
              <Checkbox checked={form.is_private} onCheckedChange={(v) => setForm({ ...form, is_private: !!v })} />
              <span className="text-sm">Privater Channel</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="chat-new-save" onClick={createChannel}>Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
